import React, { useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../../services/api";

const getValidToken = () => {
  const token = localStorage.getItem("token");
  const isValid =
    token && token !== "undefined" && token !== "null" && token.trim() !== "";

  if (!isValid) {
    localStorage.removeItem("token");
    return null;
  }
  return token;
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState({
    text: "",
    type: "",
  });

  const cleanEmail = useMemo(() => (email || "").trim().toLowerCase(), [email]);
  const cleanForgotPasswordEmail = useMemo(
    () => (forgotPasswordEmail || "").trim().toLowerCase(),
    [forgotPasswordEmail]
  );

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const rawFrom = location?.state?.from;
  const storedFrom =
    (() => {
      try {
        return localStorage.getItem("postLoginRedirect");
      } catch {
        return null;
      }
    })() || null;

  const fromCandidate =
    (typeof rawFrom === "string" && rawFrom.startsWith("/") ? rawFrom : null) ||
    (typeof storedFrom === "string" && storedFrom.startsWith("/") ? storedFrom : null) ||
    "/app/home";

  const notice =
    location?.state?.message ||
    (location?.state?.authRequired
      ? "Você precisa entrar para acessar essa área."
      : "");

  const pickNumber = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      const n = Number(String(v ?? "").replace(",", "."));
      if (Number.isFinite(n)) return n;
    }
    return NaN;
  };

  const pickString = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (typeof v === "string" && v.trim() !== "") return v.trim();
    }
    return "";
  };

  const isProfileComplete = (profile) => {
    if (!profile || typeof profile !== "object") return false;

    const name = pickString(profile, ["displayName", "display_name", "name", "nome"]);
    const gender = pickString(profile, ["gender", "sexo", "sex"]);
    const ageRange = pickString(profile, ["ageRange", "age_range", "faixaEtaria"]);
    const height = pickNumber(profile, [
      "height",
      "heightCm",
      "height_cm",
      "altura",
      "alturaCm",
    ]);
    const startWeight = pickNumber(profile, [
      "startWeight",
      "start_weight",
      "initialWeight",
      "initial_weight",
      "pesoInicial",
    ]);

    // Verifica se TODOS os campos da seção "Sobre Mim" foram preenchidos
    return Boolean(
      name && name.trim() !== "" &&
      gender && gender.trim() !== "" &&
      ageRange && ageRange.trim() !== "" &&
      Number.isFinite(height) && height > 0 &&
      Number.isFinite(startWeight) && startWeight > 0
    );
  };

  const extractProfile = (data) => {
    return data?.profile || data?.user?.profile || data?.data?.profile || data || null;
  };

  const pickPostLoginDestination = (complete) => {
    if (!complete) return "/app/profile";
    return "/app/home";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError("");

    if (!cleanEmail || !password) {
      setError("Preencha e-mail e senha para entrar.");
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      setError("Digite um e-mail válido para continuar.");
      return;
    }

    try {
      setIsSubmitting(true);

      const res = await api.post("/auth/login", {
        email: cleanEmail,
        password,
      });

      const token = res?.data?.token;
      if (!token) {
        setError("Não foi possível entrar agora. Tente novamente.");
        return;
      }

      localStorage.setItem("token", token);

      try {
        localStorage.removeItem("postLoginRedirect");
      } catch {
        // sem ação
      }

      let complete = false;
      try {
        const prof = await api.get("/users/profile");
        const p = extractProfile(prof?.data);

        try {
          localStorage.setItem("profile", JSON.stringify(p || {}));
        } catch {
          // sem ação
        }

        complete = isProfileComplete(p || {});
      } catch {
        complete = false;
      }

      navigate(pickPostLoginDestination(complete), { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage = err?.response?.data?.message;

      if (!err?.response) {
        setError("Não consegui conectar no servidor. Confira se o backend está rodando.");
        return;
      }

      if (status === 401) {
        setError(apiMessage || "E-mail ou senha inválidos. Verifique e tente novamente.");
        return;
      }

      setError(apiMessage || "Erro ao fazer login. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenForgotPasswordModal = () => {
    setForgotPasswordEmail(email || "");
    setForgotPasswordMessage({ text: "", type: "" });
    setIsForgotPasswordModalOpen(true);
  };

  const handleCloseForgotPasswordModal = () => {
    setIsForgotPasswordModalOpen(false);
    setForgotPasswordMessage({ text: "", type: "" });
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage({ text: "", type: "" });

    if (!cleanForgotPasswordEmail) {
      setForgotPasswordMessage({
        text: "Digite seu e-mail para continuar.",
        type: "error",
      });
      return;
    }

    if (!isValidEmail(cleanForgotPasswordEmail)) {
      setForgotPasswordMessage({
        text: "Digite um e-mail válido para recuperar sua senha.",
        type: "error",
      });
      return;
    }

    try {
      setForgotPasswordMessage({
        text: "Enviando solicitação...",
        type: "info",
      });

      const res = await api.post("/auth/forgot-password", {
        email: cleanForgotPasswordEmail,
      });

      setForgotPasswordMessage({
        text: res?.data?.message || "Solicitação enviada. Verifique sua caixa de entrada.",
        type: "success",
      });
    } catch (err) {
      const errorMsg = err?.response?.data?.message;
      setForgotPasswordMessage({
        text: errorMsg || "Erro ao processar a solicitação. Tente novamente.",
        type: "error",
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] text-[#24152F] flex flex-col justify-between p-4 sm:p-6 antialiased selection:bg-[#ECE7FA]">
      {/* Botão de Voltar */}
      <div className="w-full max-w-md mx-auto">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs font-bold text-[#6C54D8] hover:text-[#24152F] transition-colors py-2 group cursor-pointer"
        >
          <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
          Voltar para início
        </button>
      </div>

      {/* Card Principal de Login Liti */}
      <div className="w-full max-w-md mx-auto my-auto py-6">
        <div className="liti-card rounded-[28px] p-6 sm:p-8 shadow-xl border border-[#ECE7FA] bg-white">
          {/* Logo e Cabeçalho */}
          <div className="text-center mb-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#6C54D8] text-white font-black text-2xl shadow-sm mb-3">
              m
            </div>
            <h1 className="text-2xl font-bold text-[#24152F] tracking-tight font-serif">
              Entrar na Conta
            </h1>
            <p className="mt-1 text-xs text-[#6D6680] font-medium">
              Sua jornada diária de saúde e autocuidado
            </p>
          </div>

          {/* Avisos & Erros */}
          {notice && !error && (
            <div className="mb-4 rounded-2xl border border-[#ECE7FA] bg-[#ECE7FA]/60 p-3 text-center text-xs font-bold text-[#6C54D8]">
              {notice}
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-center text-xs font-semibold text-red-700 shadow-sm animate-in fade-in duration-200">
              {error}
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
                E-mail
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  disabled={isSubmitting}
                  inputMode="email"
                  autoFocus
                  required
                  className="input-base w-full h-12 text-sm font-medium"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680]">
                  Senha
                </label>
                <button
                  type="button"
                  onClick={handleOpenForgotPasswordModal}
                  className="text-xs font-bold text-[#6C54D8] hover:underline cursor-pointer"
                >
                  Esqueceu a senha?
                </button>
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  required
                  className="input-base w-full h-12 text-sm font-medium pr-12"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isSubmitting}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-[#6C54D8] transition-colors cursor-pointer"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                id="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded-md border-gray-300 text-[#6C54D8] focus:ring-[#6C54D8] accent-[#6C54D8] cursor-pointer"
              />
              <label htmlFor="remember-me" className="text-xs font-semibold text-[#24152F] cursor-pointer">
                Lembrar neste dispositivo
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full h-12 text-sm font-bold shadow-md uppercase tracking-wider flex items-center justify-center gap-2 mt-2 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Entrando...
                </>
              ) : (
                "ENTRAR"
              )}
            </button>
          </form>

          {/* Rodapé do Card */}
          <div className="mt-6 pt-5 border-t border-[#ECE7FA] text-center">
            <p className="text-xs text-[#6D6680] font-medium">
              Ainda não tem uma conta?{" "}
              <Link to="/register" className="font-bold text-[#6C54D8] hover:underline">
                Criar conta grátis
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Modal Esqueci Minha Senha */}
      {isForgotPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#24152F]/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="liti-card w-full max-w-sm rounded-[28px] p-6 shadow-2xl border border-[#ECE7FA] bg-white">
            <h3 className="text-lg font-bold text-[#24152F] font-serif">
              Recuperar Senha
            </h3>
            <p className="mt-1 text-xs text-[#6D6680] font-medium">
              Digite seu e-mail cadastrado para receber as instruções de redefinição de senha.
            </p>

            {forgotPasswordMessage.text && (
              <div
                className={`mt-3 rounded-2xl p-3.5 text-xs font-semibold text-center leading-relaxed border ${
                  forgotPasswordMessage.type === "success"
                    ? "bg-[#CBF1DB] border-emerald-300 text-emerald-900"
                    : forgotPasswordMessage.type === "info"
                    ? "bg-purple-50 border-purple-200 text-purple-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                {forgotPasswordMessage.text}
              </div>
            )}

            <form onSubmit={handleForgotPasswordSubmit} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-bold text-[#6D6680] mb-1">
                  E-mail
                </label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="input-base w-full h-10 text-xs font-medium"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleCloseForgotPasswordModal}
                  className="btn-secondary flex-1 h-9 text-xs font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 h-9 text-xs font-bold cursor-pointer"
                >
                  Enviar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer simples */}
      <div className="w-full max-w-md mx-auto text-center py-2">
        <p className="text-[11px] text-[#6D6680] font-medium">
          © {new Date().getFullYear()} MariaM — Saúde, bem-estar e resultados reais.
        </p>
      </div>
    </div>
  );
}